import { commonStrings } from "@src/constants/common-strings";
import { MainRoutes } from "@router/route-params";
import { RequiredFeatureCompanyLevelName } from "@utils/feature-control/company/company-feature-control-types";
import { ReactNode } from "react";
import {
  SettingsIcon,
  AnalyticsIcon,
  GroupsIcon,
  MembersIcon,
  ProjectsIcon,
} from "@flat-ui";

/**
 * All the items that can be seen in the sidebar.
 * The reason this was changed from an enum to a type is that typescript enums do not support
 * strings from variables (const or not)
 */
export type ESidebarItem =
  | typeof commonStrings.PROJECTS
  | typeof commonStrings.MEMBERS
  | typeof commonStrings.FEEDBACK
  | typeof commonStrings.GROUPS
  | typeof commonStrings.ANALYTICS
  | typeof commonStrings.SETTINGS
  | typeof commonStrings.HOLOBUILDER;

export interface SidebarItemProps {
  /** The unique identity of the sidebar item */
  identity: ESidebarItem;

  /** The icon to show next to the sidebar item */
  icon: ReactNode;

  /** The title of the sidebar item */
  title: ESidebarItem;

  /** The route in which the icon should navigate to */
  route: string;
  /**
   * the value used to identify each Nav link of the sideBar by a unique id
   * this value is used for our automation framework playwright
   */
  dataTestId: string;

  /**
   * Defines the required company feature subscription role that the user should have enabled and permitted,
   * if the feature is available in company context with enabled and permitted, the component
   * will show the item, otherwise it will hide it.
   */
  requiredFeatureCompanyLevel?: RequiredFeatureCompanyLevelName;

  /**
   * Optional date to show a "new" badge on the sidebar item.
   * Once the date is passed, the badge will be removed.
   */
  hideNewBadgeDate?: string;

  /**
   * Whether the sidebar item should show only in beta testing
   */
  shouldShowOnlyInBeta?: boolean;

  /**
   * Whether the sidebar item should show only in beta testing or if the environment is not production
   */
  shouldShowOnlyInBetaOrNotInProd?: boolean;

  /**
   * Whether the sidebar item should show only in alpha testing
   */
  shouldShowOnlyInAlpha?: boolean;
}

/** The items to show in the top of the sidebar */
export const SidebarItems: SidebarItemProps[] = [
  {
    identity: commonStrings.PROJECTS,
    icon: <ProjectsIcon />,
    title: commonStrings.PROJECTS,
    route: MainRoutes.projects,
    dataTestId: "SIDEBAR_PROJECT_BUTTON",
    requiredFeatureCompanyLevel:
      RequiredFeatureCompanyLevelName.canViewProjects,
  },
  {
    identity: commonStrings.MEMBERS,
    icon: <MembersIcon />,
    title: commonStrings.MEMBERS,
    route: MainRoutes.members,
    dataTestId: "SIDEBAR_USER_DIRECTORY_BUTTON",
    requiredFeatureCompanyLevel:
      RequiredFeatureCompanyLevelName.canViewCompanyMembers,
  },
  {
    identity: commonStrings.GROUPS,
    icon: <GroupsIcon />,
    title: commonStrings.GROUPS,
    route: MainRoutes.groups,
    dataTestId: "SIDEBAR_GROUPS_BUTTON",
    requiredFeatureCompanyLevel:
      RequiredFeatureCompanyLevelName.canViewCompanyGroups,
  },
  {
    identity: commonStrings.ANALYTICS,
    icon: <AnalyticsIcon />,
    title: commonStrings.ANALYTICS,
    route: MainRoutes.analytics,
    dataTestId: "SIDEBAR_ANALYTICS_BUTTON",
    requiredFeatureCompanyLevel:
      RequiredFeatureCompanyLevelName.canViewAnalytics,
  },
  {
    identity: commonStrings.SETTINGS,
    icon: <SettingsIcon />,
    title: commonStrings.SETTINGS,
    route: MainRoutes.settings,
    dataTestId: "SIDEBAR_WORKSPACE_SETTINGS_BUTTON",
    requiredFeatureCompanyLevel:
      RequiredFeatureCompanyLevelName.canViewWorkspaceSettings,
  },
];
